import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { fetchContractorLogos } from "../../utils/api";
import ContractorLogo from "../common/ContractorLogo";

const partners = [
  {
    name: "Hardings Ready Mix",
    url: "https://hardingsreadymix.co.uk/",
    logo: "https://hardingsreadymix.co.uk/wp-content/uploads/2025/03/hardings.png",
  },
  {
    name: "Aylesbury Plant Hire",
    url: "https://www.aylesburyplanthire.com/",
    logo: "https://xahioo7ns6.cloudimg.io/https://www.abnixsolutions.com/static/logos/logo-2-65f830685c719366164984-removebg-preview-662606120cf09808332161.png",
  },
  {
    name: "Mason Grab Hire",
    url: "https://www.masongrabhire.com/",
    logo: "https://images.squarespace-cdn.com/content/v1/6375362e95c35e07c6a7dbf3/41cfd461-f261-4b1d-82df-bd0a89b954a7/Grab+Hire.jpeg?format=1500w",
  },
  {
    name: "Landmark Auctions",
    url: "https://landmarkauctions.co.uk",
    logo: "https://landmarkauctions.co.uk/wp-content/uploads/2022/06/LandmarkAuctions_Logo.png",
  },
  {
    name: "Numbers Ltd",
    url: "https://www.numbers-ltd.co.uk",
    logo: "https://www.numbers-ltd.co.uk/img/logo-light.svg",
    darkBg: true,
  },
];

const PartnerCard = ({ partner, ariaHidden }) => (
  <a
    href={partner.url}
    target="_blank"
    rel="noopener noreferrer"
    className="contractor-logo-wrapper"
    draggable="false"
    aria-hidden={ariaHidden}
    tabIndex={ariaHidden ? -1 : undefined}
  >
    <div
      className={`contractor-logo-card${partner.darkBg ? " contractor-logo-card--dark" : ""}`}
    >
      <ContractorLogo
        contractor={{ business_name: partner.name, business_logo: partner.logo }}
        imgClassName="contractor-logo-img"
        size="md"
        alt=""
      />
    </div>
    <span className="contractor-logo-name">{partner.name}</span>
  </a>
);

const TradesPersonCard = ({ contractor, ariaHidden }) => (
  // Links to the listing on our own site, not the firm's website. These are
  // directory listings, many of them unclaimed — sending homepage traffic
  // off to them was giving away visitors and implying a relationship.
  <Link
    to={`/contractor-search/${contractor.id}`}
    className="contractor-logo-wrapper"
    draggable="false"
    title={contractor.business_name || "Contractor"}
    aria-hidden={ariaHidden}
    tabIndex={ariaHidden ? -1 : undefined}
  >
    <div className="contractor-logo-card">
      <ContractorLogo
        contractor={contractor}
        imgClassName="contractor-logo-img"
        size="md"
        alt=""
      />
    </div>
    <span className="contractor-logo-name">
      {contractor.business_name || "Contractor"}
    </span>
  </Link>
);

const PartnersSection = () => {
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [requestAttempt, setRequestAttempt] = useState(0);
  const tradesHeadingRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();
    const loadContractorLogos = async () => {
      try {
        const response = await fetchContractorLogos({ signal: controller.signal });
        if (controller.signal.aborted) return;
        const contractorList = response.contractor_logos;
        if (!Array.isArray(contractorList)) throw new Error("Invalid contractor listings response");
        setContractors(contractorList);
        setLoadError(false);
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error("Failed to fetch contractor logos:", error);
        setLoadError(true);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
          if (requestAttempt > 0) tradesHeadingRef.current?.focus();
        }
      }
    };

    loadContractorLogos();
    return () => controller.abort();
  }, [requestAttempt]);

  return (
    <section className="partners-section">
      <div className="partners-content">
        <div className="partners-header">
          <h2 className="section-title">
            Our Partners & Tradespeople Near You
          </h2>
          <p className="partners-subtitle">
            Explore supplier links and tradesperson profiles, then check their
            services, evidence and availability for your area.
          </p>
        </div>

        <div className="partners-block">
          <h3 className="partners-subheading">Our Partners</h3>
          <div
            id="partners-scroller-container"
            className="scroller-container scroller-fade-mask"
          >
            <div className="scroller-inner">
              <div className="scroller-content">
                <div className="partners-logo-row">
                  {partners.map((partner, index) => (
                    <PartnerCard key={`partner-${index}`} partner={partner} />
                  ))}
                </div>
                <div className="partners-logo-row" aria-hidden="true">
                  {partners.map((partner, index) => (
                    <PartnerCard
                      key={`partner-dup-${index}`}
                      partner={partner}
                      ariaHidden
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="partners-divider"></div>

        <div className="partners-block">
          <h3 className="partners-subheading" ref={tradesHeadingRef} tabIndex={-1}>Tradespeople On Our Site</h3>
          <div
            id="tradespeople-scroller-container"
            className="scroller-container scroller-fade-mask"
          >
            <div className="scroller-inner">
              <div className={`scroller-content${loading || loadError || contractors.length === 0 ? " scroller-state" : ""}`}>
                {loadError ? (
                  <div className="partners-empty-wrapper">
                    <div className="partners-empty-card partners-load-error">
                      <p role={loading ? "status" : "alert"}>
                        {loading ? "Loading tradespeople..." : "We couldn't load tradespeople. Please try again."}
                      </p>
                      <button
                        type="button"
                        className="button button-secondary"
                        disabled={loading}
                        onClick={() => {
                          setLoading(true);
                          setRequestAttempt((attempt) => attempt + 1);
                        }}
                      >
                        Retry tradespeople
                      </button>
                    </div>
                  </div>
                ) : loading ? (
                  <div className="partners-logo-row" role="status" aria-label="Loading tradespeople">
                    {[...Array(6)].map((_, index) => (
                      <div key={`loading-${index}`} className="partners-skeleton">
                        <div className="partners-skeleton-img"></div>
                        <div className="partners-skeleton-text"></div>
                      </div>
                    ))}
                  </div>
                ) : contractors.length > 0 ? (
                  <>
                    <div className="partners-logo-row">
                      {contractors.map((contractor, index) => (
                        <TradesPersonCard
                          key={`contractor-${contractor.id}-${index}`}
                          contractor={contractor}
                        />
                      ))}
                    </div>
                    <div className="partners-logo-row" aria-hidden="true">
                      {contractors.map((contractor, index) => (
                        <TradesPersonCard
                          key={`contractor-dup-${contractor.id}-${index}`}
                          contractor={contractor}
                          ariaHidden
                        />
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="partners-empty-wrapper">
                    <div className="partners-empty-card">
                      <i className="fas fa-users partners-empty-icon"></i>
                      <span className="partners-empty-text">
                        More tradespeople coming soon!
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PartnersSection;
